import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { setupAxiosInterceptors } from '../axiosConfig';
import { LogoFull } from '../components/Logo';
import './LoginPage.css';

function LoginPage({ setIsAuthenticated }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Estado 2FA
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const totpRef = useRef(null);

  useEffect(() => {
    const savedUser = sessionStorage.getItem('portalUsername');
    if (savedUser) setUsername(savedUser);
  }, []);

  useEffect(() => {
    if (step === '2fa' && totpRef.current) totpRef.current.focus();
  }, [step]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/auth/validate', { username, password });

      if (response.data.requires2FA) {
        setTempToken(response.data.tempToken);
        setStep('2fa');
        return;
      }

      finishLogin(response.data.user, response.data.expiresAt);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.details ||
        'Erro ao autenticar. Verifique as credenciais.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    if (totpCode.replace(/\s/g, '').length < 6) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/auth/2fa/verify', { tempToken, code: totpCode });
      if (response.data.success) {
        finishLogin(response.data.user, response.data.expiresAt);
      }
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || 'Código inválido.');
      setTotpCode('');
      if (data?.expired) {
        setStep('credentials');
        setTempToken('');
      } else if (data?.tempToken) {
        setTempToken(data.tempToken);
      }
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = (user, expiresAt) => {
    sessionStorage.setItem('portalUsername', user);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('sessionExpiry', String(expiresAt));
    setupAxiosInterceptors();
    setIsAuthenticated(true);
  };

  const backToCredentials = () => {
    setStep('credentials');
    setTempToken('');
    setTotpCode('');
    setError('');
  };

  // Formata automaticamente 6 dígitos com espaço no meio (123 456)
  const handleTotpChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(raw);
    if (raw.length === 6) {
      setTimeout(() => document.getElementById('totp-submit')?.click(), 80);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <LogoFull height={64} />
        </div>

        {error && <div className="error-message">{error}</div>}

        {step === 'credentials' ? (
          <form onSubmit={handleCredentials} className="login-form">
            <input
              type="text"
              placeholder="Usuário"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              className="form-input"
              autoFocus
            />
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="form-input"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <button
              type="submit"
              className="login-btn"
              disabled={loading || !username || !password}
            >
              {loading ? 'Autenticando...' : 'Conectar'}
            </button>
          </form>
        ) : (
          <form onSubmit={handle2FA} className="login-form">
            <div className="totp-header">
              <span className="totp-icon">🔐</span>
              <div>
                <div className="totp-title">Verificação em dois fatores</div>
                <div className="totp-subtitle">Abra o Google Authenticator e insira o código de 6 dígitos</div>
              </div>
            </div>
            <input
              id="totp-input"
              ref={totpRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="000000"
              value={totpCode}
              onChange={handleTotpChange}
              className="form-input totp-input"
              maxLength={6}
              autoComplete="one-time-code"
              disabled={loading}
            />
            <button
              id="totp-submit"
              type="submit"
              className="login-btn"
              disabled={loading || totpCode.length < 6}
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
            <button
              type="button"
              className="totp-back-btn"
              onClick={backToCredentials}
              disabled={loading}
            >
              ← Voltar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
